import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Globe, CalendarClock } from "lucide-react";
import { WorldClockConvert } from "./WorldClockConvert";

const ZONES = [
  { id: "bj", tz: "Asia/Shanghai", zh: "北京", en: "Beijing", es: "Pekín" },
  { id: "la", tz: "America/Los_Angeles", zh: "洛杉矶", en: "Los Angeles", es: "Los Ángeles" },
];
const WD = { zh: ["周日", "周一", "周二", "周三", "周四", "周五", "周六"], en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"], es: ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"] };

const partsOf = (d, tz) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: tz, hour12: false, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", weekday: "short", timeZoneName: "short" }).formatToParts(d).map((x) => [x.type, x.value]));
  const hh = p.hour === "24" ? "00" : p.hour;
  return { hm: `${hh}:${p.minute}`, s: p.second, md: `${p.month}/${p.day}`, wd: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(p.weekday), abbr: p.timeZoneName, wall: Date.UTC(+p.year, +p.month - 1, +p.day, +hh, +p.minute, +p.second) };
};

/* 顶栏两地时钟（北京 / 洛杉矶）：作为双城时间的统一常驻入口，随公共顶栏吸顶。
 * 平板和窄笔记本用两行城市时间节省宽度，仍保留城市名，避免只显示数字无法区分时区。
 * 点击展开秒 / 日期 / 时差和会议换算；仅内部员工，手机继续使用设置面板入口。
 */
// embedded 用于窄屏设置面板：保留两地时间功能，展开内容参与正常排版，避免弹出层越出手机边缘。
export const WorldClock = ({ embedded = false }) => {
  const { user, lang } = useApp();
  const { pathname } = useLocation();
  const L = lang === "en" ? "en" : lang === "es" ? "es" : "zh";
  const [now, setNow] = useState(() => new Date());
  const [open, setOpen] = useState(false);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  if (!user || user.role === "subcontractor" || pathname.startsWith("/portal")) return null;

  const z = ZONES.map((zn) => ({ ...zn, ...partsOf(now, zn.tz) }));
  const diffH = Math.round((z[0].wall - z[1].wall) / 36e5);
  const diffTxt = L === "zh" ? `北京比洛杉矶快 ${diffH} 小时 · 洛杉矶${z[1].abbr === "PDT" ? "夏令时" : "冬令时"} ${z[1].abbr}` : L === "es" ? `Pekín +${diffH} h · ${z[1].abbr}` : `Beijing is ${diffH}h ahead · ${z[1].abbr}`;

  return (
    <div className={embedded ? "relative" : "relative hidden shrink-0 sm:block"} data-testid="world-clock" data-open={open ? "1" : "0"} data-no-i18n>
      <button type="button" data-testid="world-clock-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        title={L === "zh" ? "两地时间 · 点击查看秒 / 日期 / 会议时间换算" : L === "es" ? "Hora mundial · clic para convertir" : "World clock · click for seconds / dates / meeting converter"}
        className={`flex items-center gap-1.5 text-sm font-medium rounded-full px-2.5 py-1.5 transition-colors tabular-nums whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${open ? "text-brand-600 bg-brand-50" : "text-slate-600 hover:text-brand-600 hover:bg-slate-100"}`}>
        <Globe className={embedded ? "h-4 w-4 shrink-0" : "hidden xl:block h-4 w-4 shrink-0"} aria-hidden="true" />
        <span className={embedded ? "flex items-center gap-1.5" : "grid gap-0.5 text-xs xl:flex xl:items-center xl:gap-1.5 xl:text-sm"}>
          {z.map((x, i) => <span key={x.id} className="inline-flex items-center justify-between gap-1">{i > 0 && <span className={embedded ? "text-slate-300" : "hidden xl:inline text-slate-300"}>·</span>}<span className="text-slate-500">{x[L]}</span><span className="font-semibold" data-testid={`world-clock-${x.id}-time`}>{x.hm}</span></span>)}
        </span>
      </button>
      {open && (
        <>
          {!embedded && createPortal(<div className="fixed inset-0 z-10" data-testid="world-clock-backdrop" onClick={() => setOpen(false)} />, document.body)}
          {/* 窄屏时从按钮左缘展开，防止面板伸到左侧导航下面；宽屏保留原来的右对齐。 */}
          <div className={`${embedded ? "relative w-full" : "absolute left-0 xl:left-auto xl:right-0 top-full w-[300px] max-w-[92vw]"} mt-2 rounded-xl border border-slate-200 bg-white shadow-xl z-50 text-slate-800 p-3 animate-in fade-in slide-in-from-top-1`} data-testid="world-clock-panel">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 mb-2"><CalendarClock className="h-3.5 w-3.5 text-brand-500" />{L === "zh" ? "两地时间 · 会议时间换算" : L === "es" ? "Hora mundial · convertir" : "World clock · meeting converter"}</p>
            <div className="space-y-1.5">
              {z.map((x) => (
                <div key={x.id} data-testid={`world-clock-row-${x.id}`} className="flex items-baseline gap-2.5 rounded-lg bg-slate-50 px-2.5 py-1.5">
                  <span className="min-w-[3rem] text-[11px] font-semibold text-slate-500 shrink-0 whitespace-nowrap">{x[L]}</span>
                  <span className="text-xl font-bold tabular-nums tracking-tight leading-none">{x.hm}<span className="text-sm font-semibold text-brand-500 ml-0.5" data-testid={`world-clock-${x.id}-sec`}>:{x.s}</span></span>
                  <span className="ml-auto text-[11px] text-slate-500 tabular-nums whitespace-nowrap" data-testid={`world-clock-${x.id}-date`}>{WD[L][x.wd]} {x.md}</span>
                </div>
              ))}
              <p data-testid="world-clock-diff" data-hours={diffH} className="text-[10px] text-slate-400 px-1">{diffTxt}</p>
            </div>
            <WorldClockConvert zones={ZONES} L={L} />
          </div>
        </>
      )}
    </div>
  );
};
